import {
  useWSModalCustom,
  WSAlert,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayoutSettings } from "../../../query/payments/queries";
import { FromTo } from "../../../shared/components/FromTo";
import { selectorIsPlatformBalanceAccount } from "../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { LabelMethod } from "./LabelMethod";
import { MethodValue } from "./ManageMethods/types";

type Props = {
  previousMethod: MethodValue;
  newMethod: MethodValue;
  onSave: () => void;
} & WSModalCustomProps<void>;

export const ModalConfirmPayoutMethodChange: React.FC<Props> = ({
  onSave,
  previousMethod,
  newMethod,
  ...modalProps
}) => {
  const userId = useUserId();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const platformBalance = useMemo(
    () =>
      (queryInternalAccounts.data || []).find(internalAccount =>
        selectorIsPlatformBalanceAccount(
          internalAccount,
          queryPayoutSettings.data
        )
      ),
    [queryInternalAccounts.data, queryPayoutSettings.data]
  );

  const isPlatformBalanceUnSelected = useMemo(() => {
    return previousMethod.id === platformBalance?.internalAccountId;
  }, [platformBalance?.internalAccountId, previousMethod.id]);

  return (
    <WSModal
      title="Change default payout method"
      size="M"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Tertiary",
            label: "Cancel",
            onClick: () => {
              modalProps.onClose();
            }
          },
          {
            label: "Save changes",
            onClick: () => {
              onSave();
              modalProps.onClose();
            }
          }
        ]
      }}
      {...modalProps}
    >
      <WSList gap="L">
        <WSText.ParagraphSm color="gray500">
          Are you sure you want to change the default payout method?
        </WSText.ParagraphSm>

        <FromTo
          fromLabel="From"
          fromContent={<LabelMethod {...previousMethod} />}
          toLabel="To"
          toContent={<LabelMethod {...newMethod} />}
        />

        {isPlatformBalanceUnSelected && (
          <WSAlert
            title="Change default payout method"
            theme="info"
            icon="info-circle"
          >
            <WSInfoBox transparent>
              <p>
                When the Platform Balance is not the default payout method, it
                will no longer store your earnings and any remaining balance
                will be sent to your new default payout method (bank account).
              </p>
              <p>
                If you select Platform Balance as your default payout method in
                the future, you will be prompted to select a preferred currency
                again.
              </p>
            </WSInfoBox>
          </WSAlert>
        )}
      </WSList>
    </WSModal>
  );
};

export const useModalConfirmPayoutMethodChange = () =>
  useWSModalCustom(ModalConfirmPayoutMethodChange);
